/**
 * Carrier design tokens — single source of truth.
 * Source: apps/landing-mcp/marketing/CARRIER-BRAND.md §7 Visual identity
 *
 * Surface elevation model (claude.ai-inspired, adapted for Carrier dark theme):
 *   surface-0  = base page background   #080C16
 *   surface-1  = raised card / panel    rgba(15, 20, 34, 0.80)
 *   surface-2  = elevated popover       rgba(22, 28, 46, 0.90)
 *   surface-3  = overlay / sheet        rgba(31, 38, 56, 0.95)
 * Elevation is expressed via alpha blending on the ink palette, not drop-shadows.
 */

export const colors = {
  /** Base page background — deepest ink */
  backgroundDark: "#080C16",
  backgroundDarkOklch: "oklch(0.09 0.02 260)",

  /** Surface elevation ladder — alpha blends, no drop-shadows */
  surface0: "#080C16",
  surface1: "rgba(15, 20, 34, 0.80)",
  surface2: "rgba(22, 28, 46, 0.90)",
  surface3: "rgba(31, 38, 56, 0.95)",

  /** Legacy alias — kept for backward compat */
  surfaceDark: "#0F1422",
  surfaceCard: "rgba(15, 20, 34, 0.75)",

  /** Borders */
  borderCard: "#1F2638",
  borderMuted: "rgba(255, 255, 255, 0.07)",
  borderSubtle: "rgba(255, 255, 255, 0.04)",

  /** Brand accent — Carrier Flame orange (do not replace with violet) */
  accentFlame: "#FF6B35",
  accentEmber: "#D9461C",
  accentSpark: "#FFB088",

  /** Legacy violet/fuchsia — used on esimmcp co-brand surface only */
  accentViolet: "#a78bfa",
  accentFuchsia: "#e879f9",

  /** Text hierarchy — tight ratio, generous contrast */
  textPrimary: "#F5F1EA",
  textSecondary: "#C9CCD6",
  textMuted: "#8A92A8",
  textFaint: "#5A6278",

  /** Status — do not deviate */
  statusSuccess: "#10b981",
  statusWarning: "#f59e0b",
  statusError: "#ef4444",

  /** Light mode equivalents */
  light: {
    background: "#ffffff",
    backgroundSecondary: "#f8fafc",
    surfaceCard: "rgba(248, 250, 252, 0.9)",
    borderCard: "#e2e8f0",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
  },
} as const;

export const gradients = {
  /**
   * Primary CTA button gradient — flame orange, subtle, claude.ai-style.
   * Direction 160deg reads left-to-right on wide buttons without feeling gaudy.
   */
  buttonPrimary: "linear-gradient(160deg, #FF7A45 0%, #FF6B35 55%, #E85D28 100%)",
  buttonPrimaryHover: "linear-gradient(160deg, #FF8555 0%, #FF7340 55%, #F06630 100%)",

  /** Ghost button — ultra-subtle surface highlight */
  buttonGhost: "linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
  buttonGhostHover: "linear-gradient(160deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.05) 100%)",

  /** Co-brand accent (esimmcp violet→fuchsia) */
  accentLinear: "linear-gradient(135deg, #a78bfa 0%, #e879f9 100%)",
  accentLinearDeg: (deg: number) =>
    `linear-gradient(${deg}deg, #a78bfa 0%, #e879f9 100%)`,
  accentText: "linear-gradient(135deg, #a78bfa 0%, #e879f9 100%)",

  /** Flame text gradient */
  flameText: "linear-gradient(135deg, #FF6B35 0%, #FFB088 100%)",

  /** Subtle glow — flame tint */
  glowFlame: "radial-gradient(ellipse at center, rgba(255, 107, 53, 0.15) 0%, transparent 70%)",
  /** Subtle glow — violet (esimmcp) */
  glowViolet: "radial-gradient(ellipse at center, rgba(167, 139, 250, 0.15) 0%, transparent 70%)",
} as const;

export const typography = {
  fontSans: "var(--font-sans, Inter), system-ui, -apple-system, sans-serif",
  fontMono: "var(--font-mono, 'JetBrains Mono'), ui-monospace, monospace",
  fontSerif: "var(--font-serif, Georgia), 'Times New Roman', serif",

  /** Weights — only these three, per brand guide */
  weightRegular: "400",
  weightBold: "700",
  weightBlack: "900",

  /** Line heights */
  lineHeightHeadline: "1.05",
  lineHeightSubheading: "1.2",
  lineHeightBody: "1.5",

  /** Letter spacing — tight on big headlines */
  trackingTight: "-0.04em",
  trackingNormal: "0em",
  trackingWide: "0.05em",

  /** Type scale (rem) */
  scale: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
  },
} as const;

export const spacing = {
  /** Section vertical padding — 80px / 112px */
  sectionSm: "5rem",
  sectionLg: "7rem",

  /** Content max-width */
  contentMax: "72rem",
  contentNarrow: "48rem",
  contentWide: "90rem",
} as const;

export const radius = {
  sm: "0.375rem",
  md: "0.5rem",
  lg: "0.75rem",
  xl: "1rem",
  "2xl": "1.5rem",
  full: "9999px",
} as const;

export const motion = {
  /** Duration */
  durationFast: "150ms",
  durationBase: "250ms",
  durationSlow: "400ms",
  durationSlower: "700ms",

  /** Easing */
  easingDefault: "cubic-bezier(0.4, 0, 0.2, 1)",
  easingIn: "cubic-bezier(0.4, 0, 1, 1)",
  easingOut: "cubic-bezier(0, 0, 0.2, 1)",
  easingSpring: "cubic-bezier(0.34, 1.56, 0.64, 1)",

  /** Common transitions */
  transitionBase: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
  transitionFast: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
  transitionColor: "color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

export const shadows = {
  /**
   * Elevation expressed as glow, not drop-shadow — claude.ai principle.
   * No box-shadow: 0 4px 6px rgba(0,0,0,0.x) on interactive elements.
   */
  flameGlow: "0 0 32px rgba(255, 107, 53, 0.18)",
  flameGlowStrong: "0 0 48px rgba(255, 107, 53, 0.28)",
  cardGlow: "0 0 40px rgba(255, 107, 53, 0.10)",
  cardGlowStrong: "0 0 60px rgba(255, 107, 53, 0.18)",
  innerHighlight: "inset 0 1px 0 rgba(255, 255, 255, 0.06)",
  innerHighlightStrong: "inset 0 1px 0 rgba(255, 255, 255, 0.12)",

  /** Legacy violet glow (esimmcp surface) */
  cardGlowViolet: "0 0 40px rgba(167, 139, 250, 0.12)",
} as const;

export const tokens = {
  colors,
  gradients,
  typography,
  spacing,
  radius,
  motion,
  shadows,
} as const;

export type Tokens = typeof tokens;
